import { IconButton } from "@material-ui/core";
import CreateIcon from "@material-ui/icons/Create";
import { useTranslations } from "adapters/i18n";
import { CustomTableColumn } from "features/shared/components";
import SwitchCustomizado from "features/shared/components/SwitchCustomizado";
import React, { useMemo } from "react";
import type { Goal } from "shared/types";
import { LotGoalListTableColumn } from "../utils/lotGoal.utils";

type UseLotGoalsTableProps = {
  isFormPanelOpen: boolean;
  onEditClick: (row: Goal) => void;
  onToggleStatus: (row: Goal, isCurrentDeleted: boolean) => void;
};

export function useLotGoalsTable({
  isFormPanelOpen,
  onEditClick,
  onToggleStatus,
}: UseLotGoalsTableProps) {
  const { t } = useTranslations();

  const columns = useMemo<
    CustomTableColumn<Goal, typeof LotGoalListTableColumn>[]
  >(
    () => [
      {
        id: LotGoalListTableColumn.Name,
        label: t("livestock:livestock-goal"),
        isSortable: true,
        type: "text",
        getRowValue: (row) => row.name,
      },
      {
        id: LotGoalListTableColumn.Edit,
        label: t("common:actions.edit"),
        isSortable: false,
        isActionColumn: true,
        type: "custom",
        renderCell: (row) => (
          <EditCell
            isFormPanelOpen={isFormPanelOpen}
            onEditClick={onEditClick}
            row={row}
          />
        ),
      },
      {
        id: LotGoalListTableColumn.Status,
        label: t("common:status.active"),
        isSortable: false,
        isActionColumn: true,
        type: "custom",
        renderCell: (row) => (
          <StatusCell onToggleStatus={onToggleStatus} row={row} />
        ),
      },
    ],
    [isFormPanelOpen, onEditClick, onToggleStatus, t]
  );

  return { columns };
}

type EditCellProps = {
  isFormPanelOpen: boolean;
  onEditClick: (row: Goal) => void;
  row: Goal;
};

function EditCell({ isFormPanelOpen, onEditClick, row }: EditCellProps) {
  const { t } = useTranslations();

  const handleEditClick = () => {
    onEditClick(row);
  };

  return (
    <IconButton
      aria-label={t("common:actions.edit")}
      data-testid="edit-goal-button"
      disabled={isFormPanelOpen}
      onClick={handleEditClick}
    >
      <CreateIcon />
    </IconButton>
  );
}

type StatusCellProps = {
  onToggleStatus: (row: Goal, isCurrentDeleted: boolean) => void;
  row: Goal;
};

function StatusCell({ onToggleStatus, row }: StatusCellProps) {
  const isDeleted = row.deletedAt != null || row.deletionUser != null;

  const handleToggle = () => {
    onToggleStatus(row, isDeleted);
  };

  return (
    <SwitchCustomizado defaultChecked={!isDeleted} handleClick={handleToggle} />
  );
}
